import React, { useState } from 'react';
import { supportService } from '../services/api';
import { showSuccess, showError } from '../utils/toast';
import './SupportModal.css';

/**
 * #260 — Modal "Falar com o suporte".
 *
 * Aberto a partir do cabeçalho (UserHeader), disponível em toda tela logada.
 * Formulário curto: Assunto, Tipo (opcional) e Mensagem. O backend anexa o
 * contexto da sessão (usuário, tenant, plano) e usa o e-mail do usuário como
 * Reply-To — por isso o usuário só digita o essencial.
 *
 * Props:
 *   isOpen   — controla visibilidade
 *   onClose  — fecha o modal
 *   user     — objeto user atual (usado só pra exibir o e-mail de resposta)
 */
function SupportModal({ isOpen, onClose, user }) {
  const [assunto, setAssunto] = useState('');
  const [tipo, setTipo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  if (!isOpen) return null;

  const fechar = () => {
    if (enviando) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const a = (assunto || '').trim();
    const m = (mensagem || '').trim();
    if (!a) {
      showError('Informe um assunto.');
      return;
    }
    if (!m) {
      showError('Escreva sua mensagem.');
      return;
    }
    setEnviando(true);
    try {
      await supportService.enviarSuporte({
        assunto: a,
        mensagem: m,
        tipo: tipo || undefined,
        pagina: window.location.pathname,
      });
      showSuccess('Mensagem enviada — responderemos por e-mail.');
      // Limpa e fecha
      setAssunto('');
      setTipo('');
      setMensagem('');
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showError(detail || 'Não foi possível enviar sua mensagem. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div
      className="support-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-title"
      onClick={fechar}
    >
      <div className="support-content" onClick={(e) => e.stopPropagation()}>
        <div className="support-header">
          <h3 id="support-title">
            <span
              className="material-symbols-outlined"
              style={{ fontSize: '20px', verticalAlign: 'text-bottom' }}
            >
              support_agent
            </span>
            {' Falar com o suporte'}
          </h3>
          <button
            type="button"
            className="support-close"
            onClick={fechar}
            disabled={enviando}
            aria-label="Fechar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form className="support-body" onSubmit={handleSubmit}>
          <label htmlFor="support-assunto">Assunto</label>
          <input
            id="support-assunto"
            type="text"
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
            disabled={enviando}
            placeholder="Resumo do que você precisa"
            maxLength={200}
            autoFocus
          />

          <label htmlFor="support-tipo">Tipo (opcional)</label>
          <select
            id="support-tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            disabled={enviando}
          >
            <option value="">Selecione...</option>
            <option value="duvida">Dúvida</option>
            <option value="problema">Problema</option>
            <option value="sugestao">Sugestão</option>
          </select>

          <label htmlFor="support-mensagem">Mensagem</label>
          <textarea
            id="support-mensagem"
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            disabled={enviando}
            placeholder="Descreva sua dúvida, problema ou sugestão"
            rows={5}
            maxLength={5000}
          />

          {user?.email && (
            <p className="support-reply-hint">
              Responderemos no seu e-mail: <strong>{user.email}</strong>
            </p>
          )}

          <button type="submit" className="support-btn-primary" disabled={enviando}>
            {enviando ? 'Enviando...' : 'Enviar mensagem'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SupportModal;
